'use client';

import { BlockContent } from '@app/sanity.types';
import styles from '@styles/jumbotron.module.scss';
import { PortableText, type PortableTextComponents } from 'next-sanity';
import React, { useState } from 'react';

type Props = {
  desc: string | BlockContent;
};

/**
 * The page's own `<h1>` is the Jumbotron title rendered directly above this description. Editors
 * routinely open a Sanity description with an `h1` block, which put a second `<h1>` into the served
 * HTML on pages such as /boats/category/catamaran-boats and /boats/activity/sailing.
 *
 * Demoting to `<h2>` keeps the description subordinate to the title it sits under. Only `h1` is
 * remapped, so the rest of an editor's heading hierarchy is left as authored.
 */
const descriptionComponents: PortableTextComponents = {
  block: {
    h1: ({ children }) => <h2>{children}</h2>,
  },
};

/**
 * Heading blocks an editor left with no text in them.
 *
 * Sanity keeps a heading block in the document once its style is set, even after the text is
 * deleted, so an abandoned heading ships as `<h2></h2>`. That is what put "Empty heading" on maker
 * pages such as /maker/acm-marine and /maker/aquasport (MDC-4221). Dropping them here rather than
 * in the CMS means the fix holds for any description an editor writes next.
 *
 * Only headings are dropped. An empty normal block is how editors write a blank line, and removing
 * those would change the spacing an editor deliberately set.
 */
const HEADING_STYLES = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

const isEmptyHeadingBlock = (block: unknown): boolean => {
  if (typeof block !== 'object' || block === null) {
    return false;
  }

  const { _type, style, children } = block as { _type?: string; style?: string; children?: { text?: string }[] };

  if (_type !== 'block' || !style || !HEADING_STYLES.has(style)) {
    return false;
  }

  return !children?.some((child) => (child?.text ?? '').trim().length > 0);
};

const JumbotronDescription: React.FC<Props> = ({ desc }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={styles.containerDesc}>
      <div className={expanded ? styles.descContent : styles.descContentClamped}>
        {typeof desc === 'string' ? (
          // eslint-disable-next-line react/no-danger
          <div> {desc} </div>
        ) : (
          <PortableText
            value={desc.filter((block) => !isEmptyHeadingBlock(block))}
            components={descriptionComponents}
          />
        )}
      </div>
      <button type="button" className={styles.toggleLabel} onClick={() => setExpanded((prev) => !prev)}>
        {expanded ? 'Show less' : 'Show more'}
      </button>
    </div>
  );
};

export default JumbotronDescription;
